import React, { useMemo } from 'react';
import classNames from 'classnames';
import { BaseInputProps } from './BaseInput';

/**
 * Read-only renderer for `PropertyValueType.JSON` properties.
 *
 * JSON properties hold an arbitrary JSON-encoded value (string under the
 * hood). The auto-generated PropertyPanel previously rendered them as
 * "Unsupported parameter type"; this fallback gives a pretty-printed view so
 * the values are at least visible. Edits are intentionally disabled — typing
 * raw JSON into a one-line input field is a foot-gun and every JSON property
 * we ship today is computed by tools (skeleton edges, opening_room_ids).
 * When a JSON property genuinely needs user editing, register a dedicated
 * custom component via `propertyRegistry.setCustomComponent`.
 */
export const JsonInput: React.FC<BaseInputProps<string>> = ({ instance, className }) => {
  const display = useMemo(() => {
    const raw = instance.value;
    if (raw === null || raw === undefined) return '';
    if (typeof raw !== 'string') {
      // Defensive — the type system says STRING, but tools occasionally
      // assign an already-parsed value via TypedJsonProperty. Stringify it
      // so we never crash JSON.parse on a non-string.
      try {
        return JSON.stringify(raw, null, 2);
      } catch {
        return String(raw);
      }
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }, [instance.value]);

  return (
    <pre
      className={classNames(
        'flex-1 px-2 py-1 text-xs font-mono whitespace-pre-wrap break-all',
        'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
        'border border-gray-300 dark:border-gray-700 rounded',
        'max-h-40 overflow-auto',
        className,
      )}
    >
      {display}
    </pre>
  );
};
